import CalendarComponent, { SelectedDate, buildBlockedMap } from './CalenderComponent';
import tw from 'twin.macro';
import styled from 'styled-components';
import { useEffect, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import PriceAdjustmentPanel from '../PriceCalendar/AdjustPricePanel';
import DynamicPricingPanel from '../PriceCalendar/DynamicPricingPanel';
import DynamicPricingConfirmModal from '../PriceCalendar/DynamicPricingConfirmModal';
import { useWindowWidth } from '../../hooks/useMobileScreenListenerHook';
import { IPriceCalendar } from '../../../__generated__/graphql';
import { useMutation, useQuery } from '@apollo/client';
import {
  GET_CAR,
  UPDATE_CAR_HOURLY_PRICING,
  GET_CAR_AVAILABILITIES_BY_CAR,
} from '../../services/carService/queries';
import holidays from '@date/holidays-us';
import UnavailabilityPanel from '../UnavailabilityPanel';
import NumericInput from '../NumericInput';
import { DateRangeModal } from '../bookCard/DateRangeModal';
import { getTodayInTimezone } from '../../helpers/timezoneHelpers';
import { useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
const TabContainer = styled.div`
  ${tw`
    md:relative
    w-full
    flex
    bg-bg-secondary
    p-1
    rounded-lg
    mb-4
  `}
`;

const Tab = styled.button<{ isActive?: boolean }>`
  ${tw`
    flex-1
    py-2
    px-4
    text-sm
    font-medium
    rounded-md
    transition-colors
  `}
  ${({ isActive }) => (isActive ? tw`bg-black text-white` : tw`text-text-secondary`)}

  &:hover {
    ${({ isActive }) => !isActive && tw`text-text-primary`}
  }
`;

const ContainerWrapper = styled.div`
  ${tw`w-full`}
`;

const Container = styled.div`
  ${tw`
    flex
    flex-col
    w-full
    md:flex-row
    relative
    mb-4
    md:mb-8
  `}
`;

const PriceAdjustmentPanelContainer = styled.div`
  ${tw`
    w-full
    lg:block
    md:w-80
    md:mt-0
    md:ml-4
  `}
`;

const MobileViewDetailsWrapper = styled.div`
  ${tw`
    w-full
    p-4
    mt-4
    lg:hidden
    flex
    justify-center
  `}
`;

const MobileViewDetailsButton = styled.button`
  ${tw`
    w-full
    py-3
    px-6
    bg-white
    border
    border-border-default
    rounded-lg
    text-text-primary
    font-semibold
    shadow-card
    transition-all
  `}

  &:hover {
    ${tw`bg-bg-secondary`}
  }
`;

const PanelContent = styled.div`
  ${tw`p-4`}
`;

const MobileButtonWrapper = styled.div`
  ${tw`
    w-full
    mt-4
    flex
    justify-center
    lg:hidden
  `}
`;

const DetailsColumn = styled.div`
  ${tw`w-full`}
`;

const CalendarColumn = styled.div`
  ${tw`w-full flex flex-col items-center min-w-0`}
  flex: 1 1 auto;
`;

const CalendarContainer = styled.div`
  ${tw`
    w-full
    flex
    justify-between
    items-center
    text-text-secondary
    font-bold
  `}
  ${tw`gap-3 flex-wrap`}
`;

const PanelTitle = styled.p`
  ${tw`
    text-sm
    p-2
    md:p-4
  `}
`;

const RangeButton = styled.button`
  ${tw`
    py-2
    px-3
    text-xs
    font-semibold
    border
    border-border-default
    rounded-lg
    bg-white
    text-text-secondary
    transition-colors
  `}
  &:hover {
    ${tw`bg-bg-secondary`}
  }
`;

const HourlySettingsCard = styled.div`
  ${tw``}
`;
const Row = styled.div`
  ${tw`grid grid-cols-1 md:grid-cols-4 gap-4 items-end my-4 w-full`}
`;
const Label = styled.label`
  ${tw`text-sm font-medium text-text-secondary mb-1 block`}
`;
const Input = styled(NumericInput)`
  ${tw`p-2 border rounded-md w-full`}
`;
const SwitchRow = styled.div`
  ${tw`flex items-center gap-3 mb-3`}
`;
const SaveButton = styled.button`
  ${tw`py-2 px-4 bg-black text-white rounded-md w-full transition-colors`}
  &:hover {
    ${tw`bg-blue-700`}
  }
`;

interface Booking {
  id: string;
  startAt: number;
  endAt: number;
  status: string;
}

interface Unavailability {
  id: string;
  startAt: number;
  endAt: number;
  reason?: string | null;
  note?: string | null;
}

const PriceCalendar = ({
  priceCalendar,
  carId,
  sectionTitle = 'Daily Pricing Calendar',
  modeLabel = 'daily',
  weekdayPrice,
  weekendPrice,
  holidayPrice,
  hourlyRentalIsOn: initialHourlyRentalIsOn = false,
  weekdayHrPrice: initialWeekdayHrPrice,
  weekendHrPrice: initialWeekendHrPrice,
  holidayHrPrice: initialHolidayHrPrice,
  timezone = 'America/Los_Angeles',
  bookings = [],
  unavailabilities = [],
  syncUrlOnChange = false,
  allowDynamicPricing = true,
  pricingLocked = false,
}: {
  priceCalendar: IPriceCalendar[];
  carId: string;
  sectionTitle?: string;
  modeLabel?: 'daily' | 'hourly';
  weekdayPrice?: number | null;
  weekendPrice?: number | null;
  holidayPrice?: number | null;
  hourlyRentalIsOn?: boolean;
  weekdayHrPrice?: number | null;
  weekendHrPrice?: number | null;
  holidayHrPrice?: number | null;
  timezone?: string;
  bookings?: Booking[];
  unavailabilities?: Unavailability[];
  syncUrlOnChange?: boolean;
  /** Stays (spec 016) price per night through their own settings: no dynamic tab. */
  allowDynamicPricing?: boolean;
  /**
   * Spec 014a: the site's brand turned off `carPricing`. Per-day prices and
   * weekend or holiday rates are hidden (the server refuses them); blocking
   * dates and the base daily rate stay.
   */
  pricingLocked?: boolean;
}) => {
  const [selectedDates, setSelectedDates] = useState<SelectedDate[]>([]);
  const [activeTab, setActiveTab] = useState<'prices' | 'unavailability' | 'dynamic'>(
    pricingLocked ? 'unavailability' : 'prices',
  );
  const [dynamicConfirmOpen, setDynamicConfirmOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'calendar' | 'details'>('calendar');
  const [updatingDates, setUpdatingDates] = useState<Set<string>>(new Set());
  // Days that just finished saving get a one-second success check (Flutter `_flashSuccess`).
  const [flashDates, setFlashDates] = useState<Set<string>>(new Set());
  const previousUpdatingRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const previous = previousUpdatingRef.current;
    previousUpdatingRef.current = updatingDates;
    if (previous.size > 0 && updatingDates.size === 0) {
      const finished = new Set(previous);
      setFlashDates(finished);
      const timer = setTimeout(() => setFlashDates(new Set()), 1000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [updatingDates]);
  const [isRangeModalOpen, setRangeModalOpen] = useState(false);
  const [rangeStart, setRangeStart] = useState<Date>(() => getTodayInTimezone(timezone));
  const [rangeEnd, setRangeEnd] = useState<Date>(() =>
    DateTime.fromJSDate(getTodayInTimezone(timezone)).plus({ days: 1 }).toJSDate(),
  );
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile, isTablet, isDesktop } = useWindowWidth();
  const isSmallerScreen = !isDesktop;
  const [hourlyOn, setHourlyOn] = useState<boolean>(initialHourlyRentalIsOn);
  const [weekdayHr, setWeekdayHr] = useState<number | undefined>(
    initialWeekdayHrPrice ?? undefined,
  );
  const [weekendHr, setWeekendHr] = useState<number | undefined>(
    initialWeekendHrPrice ?? undefined,
  );
  const [holidayHr, setHolidayHr] = useState<number | undefined>(
    initialHolidayHrPrice ?? undefined,
  );
  const [hrPriceSet, setHrPriceSet] = useState<boolean>(
    weekdayHr !== undefined && weekendHr !== undefined && holidayHr !== undefined,
  );

  // Fetch unavailabilities directly using the dedicated query
  const { data: availabilitiesData } = useQuery(GET_CAR_AVAILABILITIES_BY_CAR, {
    variables: { carId },
    skip: !carId,
  });
  const fetchedUnavailabilities = availabilitiesData?.carAvailabilitiesByCar || [];
  const blockedDateKeys = new Set(
    buildBlockedMap(fetchedUnavailabilities, priceCalendar ?? [], timezone).keys(),
  );
  const fallbackPrices =
    modeLabel === 'hourly'
      ? {
          weekday: initialWeekdayHrPrice,
          weekend: initialWeekendHrPrice,
          holiday: initialHolidayHrPrice,
        }
      : { weekday: weekdayPrice, weekend: weekendPrice, holiday: holidayPrice };

  const [updateHourlyPricing, { loading: saving }] = useMutation(UPDATE_CAR_HOURLY_PRICING, {
    refetchQueries: [{ query: GET_CAR, variables: { id: carId } }],
  });

  // Sync with prop changes
  useEffect(() => {
    setHourlyOn(initialHourlyRentalIsOn);
  }, [initialHourlyRentalIsOn]);

  useEffect(() => {
    setWeekdayHr(initialWeekdayHrPrice ?? undefined);
    setWeekendHr(initialWeekendHrPrice ?? undefined);
    setHolidayHr(initialHolidayHrPrice ?? undefined);
    setHrPriceSet(weekdayHr !== undefined && weekendHr !== undefined && holidayHr !== undefined);
  }, [initialWeekdayHrPrice, initialWeekendHrPrice, initialHolidayHrPrice]);

  useEffect(() => {
    // Sync with isDesktop if needed
  }, [isDesktop]);

  // Hooks must run on every render, so the URL sync sits above the early return.
  useEffect(() => {
    if (!syncUrlOnChange) return;
    if (!rangeStart || !rangeEnd) return;

    const currentParams = queryString.parse(location.search);
    const nextQuery = {
      ...currentParams,
      startDate: rangeStart.toISOString(),
      endDate: rangeEnd.toISOString(),
    };
    const nextSearch = `?${queryString.stringify(nextQuery, {
      skipNull: true,
      skipEmptyString: true,
    })}`;

    if (nextSearch !== location.search) {
      navigate({ search: nextSearch }, { replace: true });
    }
  }, [syncUrlOnChange, rangeStart, rangeEnd, location.search, navigate]);

  if (!priceCalendar || !carId) {
    return null;
  }

  const priceKey = modeLabel === 'hourly' ? 'hourPrice' : 'price';
  const priceMap = new Map(
    priceCalendar.map(item => [
      item.date,
      priceKey === 'hourPrice' ? ((item as any).hourPrice ?? item.price) : item.price,
    ]),
  );
  const availabilityMap = new Map(priceCalendar.map(item => [item.date, item.isAvailable ?? true]));

  const disabledDateList = priceCalendar
    .filter(item => item.isAvailable === false)
    .map(item => DateTime.fromISO(item.date).toJSDate());

  const disabledRangeList = [
    ...(bookings || []).map(booking => ({
      from: new Date(booking.startAt),
      to: new Date(booking.endAt),
    })),
    ...(fetchedUnavailabilities || []).map((availability: any) => ({
      from: new Date(availability.startAt),
      to: new Date(availability.endAt),
    })),
  ];

  const formatDateLabel = (date: Date) =>
    new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(date);

  const rangeLabel = `${formatDateLabel(rangeStart)} - ${formatDateLabel(rangeEnd)}`;

  const handleOpenRangeModal = () => {
    if (selectedDates.length > 0) {
      const sorted = [...selectedDates]
        .map(date => DateTime.fromISO(date.date))
        .filter(date => date.isValid)
        .sort((a, b) => a.toMillis() - b.toMillis());
      if (sorted.length > 0) {
        setRangeStart(sorted[0].toJSDate());
        setRangeEnd(sorted[sorted.length - 1].toJSDate());
      }
    }
    setRangeModalOpen(true);
  };

  const handleSaveRange = (start: Date, end: Date) => {
    const normalizedStart = start <= end ? start : end;
    const normalizedEnd = start <= end ? end : start;
    const startDt = DateTime.fromJSDate(normalizedStart).startOf('day');
    const endDt = DateTime.fromJSDate(normalizedEnd).startOf('day');

    const nextSelectedDates: SelectedDate[] = [];
    let cursor = startDt;
    while (cursor <= endDt) {
      const dateStr = cursor.toISODate() || '';
      if (dateStr) {
        const priceValue = priceMap.get(dateStr);
        nextSelectedDates.push({
          date: dateStr,
          price: priceValue !== undefined ? String(priceValue) : '0',
          isAvailable: availabilityMap.get(dateStr) ?? true,
        });
      }
      cursor = cursor.plus({ days: 1 });
    }

    setSelectedDates(nextSelectedDates);
    setRangeStart(normalizedStart);
    setRangeEnd(normalizedEnd);
    setRangeModalOpen(false);
  };

  const renderTabs = () => {
    const handleDynamicTabRequest = () => {
      if (activeTab === 'dynamic') {
        return;
      }
      setDynamicConfirmOpen(true);
    };

    return (
      <TabContainer>
        {!pricingLocked && (
          <Tab isActive={activeTab === 'prices'} onClick={() => setActiveTab('prices')}>
            Prices
          </Tab>
        )}
        <Tab
          isActive={activeTab === 'unavailability'}
          onClick={() => setActiveTab('unavailability')}
        >
          Unavailability
        </Tab>
        {modeLabel === 'daily' && allowDynamicPricing && (
          <Tab isActive={activeTab === 'dynamic'} onClick={handleDynamicTabRequest}>
            {pricingLocked ? 'Daily rate' : 'Dynamic'}
          </Tab>
        )}
      </TabContainer>
    );
  };

  const handleDynamicConfirm = () => {
    setSelectedDates([]);
    setActiveTab('dynamic');
    setDynamicConfirmOpen(false);
    if (isSmallerScreen) {
      setViewMode('details');
    }
  };

  const handleDynamicCancel = () => {
    setDynamicConfirmOpen(false);
  };

  const renderSidePanelContent = () => {
    if (activeTab === 'unavailability' || (pricingLocked && activeTab === 'prices')) {
      return (
        <UnavailabilityPanel
          carId={carId}
          selectedDates={selectedDates.map(sd => sd.date)}
          setSelectedDates={dates =>
            setSelectedDates(
              dates.map(date => ({
                date: date,
                price: '0',
                isAvailable: false,
              })),
            )
          }
          blockedDates={blockedDateKeys}
          timezone={timezone}
        />
      );
    }

    if (activeTab === 'dynamic' && modeLabel === 'daily') {
      return (
        <DynamicPricingPanel
          carId={carId}
          weekdayPrice={weekdayPrice}
          weekendPrice={weekendPrice}
          holidayPrice={holidayPrice}
          weekdayOnly={pricingLocked}
        />
      );
    }

    return (
      <>
        <PriceAdjustmentPanel
          selectedDates={selectedDates}
          setSelectedDates={setSelectedDates}
          carId={carId}
          modeLabel={modeLabel}
          priceCalendar={priceCalendar.map(p => ({
            date: p.date,
            price: p.price,
            hourPrice: (p as any).hourPrice ?? undefined,
          }))}
          onViewOnCalendar={() => setViewMode('calendar')}
          onUpdated={() => setViewMode('calendar')}
          onUpdatingDatesChange={setUpdatingDates}
        />
        {hrPriceSet && renderHourlySettings()}
      </>
    );
  };

  const renderHourlySettings = () => {
    return (
      <div className="flex flex-col gap-4">
        <Row>
          <div>
            <Label>Weekday hour price ($)</Label>
            <Input
              placeholder="e.g. 10"
              value={typeof weekdayHr === 'number' ? weekdayHr : null}
              onValueChange={val => setWeekdayHr(val === null ? undefined : val)}
              minValue={0}
            />
          </div>
          <div>
            <Label>Weekend hour price ($)</Label>
            <Input
              placeholder="e.g. 12"
              value={typeof weekendHr === 'number' ? weekendHr : null}
              onValueChange={val => setWeekendHr(val === null ? undefined : val)}
              minValue={0}
            />
          </div>
          <div>
            <Label>Holiday hour price ($)</Label>
            <Input
              placeholder="e.g. 15"
              value={typeof holidayHr === 'number' ? holidayHr : null}
              onValueChange={val => setHolidayHr(val === null ? undefined : val)}
              minValue={0}
            />
          </div>
        </Row>
        <div>
          <SaveButton
            disabled={saving}
            onClick={async () => {
              await updateHourlyPricing({
                variables: {
                  carId,
                  input: {
                    hourlyRentalIsOn: true,
                    weekdayHrPrice: typeof weekdayHr === 'number' ? weekdayHr : undefined,
                    weekendHrPrice: typeof weekendHr === 'number' ? weekendHr : undefined,
                    holidayHrPrice: typeof holidayHr === 'number' ? holidayHr : undefined,
                  },
                },
              });
            }}
          >
            {saving ? 'Saving...' : 'Save hourly settings'}
          </SaveButton>
        </div>
      </div>
    );
  };
  return (
    <ContainerWrapper>
      <CalendarContainer>
        <PanelTitle>{sectionTitle}</PanelTitle>
        <RangeButton type="button" onClick={handleOpenRangeModal}>
          {rangeLabel}
        </RangeButton>

        {modeLabel === 'hourly' && (
          <HourlySettingsCard>
            <SwitchRow>
              <input
                id="hourly-switch-off"
                type="checkbox"
                checked={hourlyOn}
                onChange={async e => {
                  const newValue = e.target.checked;
                  setHourlyOn(newValue);
                  await updateHourlyPricing({
                    variables: {
                      carId,
                      input: {
                        hourlyRentalIsOn: newValue,
                      },
                    },
                  });
                }}
              />
              <Label htmlFor="hourly-switch-off">Enable hourly rental</Label>
            </SwitchRow>
          </HourlySettingsCard>
        )}
      </CalendarContainer>
      <Container>
        {isSmallerScreen && viewMode === 'details' && (modeLabel !== 'hourly' || hourlyOn) && (
          <DetailsColumn>
            {renderTabs()}
            <PanelContent>{renderSidePanelContent()}</PanelContent>
          </DetailsColumn>
        )}

        {(isDesktop || (isSmallerScreen && viewMode === 'calendar')) && (
          <CalendarColumn>
            {(!modeLabel || modeLabel === 'daily' || (modeLabel === 'hourly' && hourlyOn)) && (
              <CalendarComponent
                priceCalendar={priceCalendar.map(p => ({
                  ...p,
                  // normalize GraphQL Maybe<number> -> number | undefined for component typing
                  hourPrice: (p as any).hourPrice ?? undefined,
                }))}
                priceKey={modeLabel === 'hourly' ? 'hourPrice' : 'price'}
                selectedDates={selectedDates}
                onSelectedDatesChange={setSelectedDates}
                bookings={bookings}
                unavailabilities={fetchedUnavailabilities}
                timezone={timezone}
                updatingDates={updatingDates}
                flashDates={flashDates}
                fallbackPrices={fallbackPrices}
              />
            )}

            {isSmallerScreen &&
              selectedDates.length > 0 &&
              (modeLabel !== 'hourly' || hourlyOn) && (
                <>
                  <MobileViewDetailsWrapper>
                    <MobileViewDetailsButton onClick={() => setViewMode('details')}>
                      View and edit details
                    </MobileViewDetailsButton>
                  </MobileViewDetailsWrapper>
                </>
              )}
          </CalendarColumn>
        )}

        {isDesktop && (modeLabel !== 'hourly' || hourlyOn) && (
          <PriceAdjustmentPanelContainer>
            {renderTabs()}
            <PanelContent>{renderSidePanelContent()}</PanelContent>
          </PriceAdjustmentPanelContainer>
        )}
      </Container>

      <DateRangeModal
        isOpen={isRangeModalOpen}
        onClose={() => setRangeModalOpen(false)}
        startDate={rangeStart}
        endDate={rangeEnd}
        onSave={handleSaveRange}
        disabledDates={disabledDateList}
        disabledRanges={disabledRangeList}
        disablePastDates={false}
      />

      <DynamicPricingConfirmModal
        isOpen={dynamicConfirmOpen}
        onConfirm={handleDynamicConfirm}
        onCancel={handleDynamicCancel}
      />
    </ContainerWrapper>
  );
};

export default PriceCalendar;
